import type { ComponentType } from "react";
import { TestResponse } from "@/types/test-response";
import { Card, CardContent } from "@/components/ui/card";
import { Info, TabletSmartphone, Clipboard } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";

interface MonitoringMetricCardsProps {
  testResponse: TestResponse;
}

export function MonitoringMetricCards({ testResponse }: MonitoringMetricCardsProps) {
  const events = testResponse.events || [];
  
  // Count events by type
  const eventCounts = events.reduce((acc: Record<string, number>, event) => {
    const type = event.event_type;
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});
  
  const windowBlurCount = eventCounts['window_blur'] || 0;
  const tabSwitchCount = eventCounts['tab_switch'] || 0;
  const clipboardCount = eventCounts['clipboard_action'] || 0;
  
  // Get color based on event count
  const getCountColor = (count: number) => {
    if (count > 5) return "text-red-500 dark:text-red-400";
    if (count > 2) return "text-amber-500 dark:text-amber-400";
    return "text-green-500 dark:text-green-400";
  };

  const MetricCard = ({ 
    title, 
    count, 
    icon: Icon, 
    tooltip 
  }: { 
    title: string; 
    count: number; 
    icon: ComponentType<any>; 
    tooltip: string;
  }) => (
    <Card className="flex-1">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">{title}</span>
          </div>
          <HoverCard>
            <HoverCardTrigger asChild>
              <span className="inline-flex">
                <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" aria-label={`Info about ${title}`} />
              </span>
            </HoverCardTrigger>
            <HoverCardContent>
              <p className="max-w-xs">{tooltip}</p>
            </HoverCardContent>
          </HoverCard>
        </div>
        <div className={`text-2xl font-bold ${getCountColor(count)}`}>
          {count}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="flex gap-4 w-full">
      <MetricCard
        title="Window Blur"
        count={windowBlurCount}
        icon={TabletSmartphone}
        tooltip="Number of times the candidate switched away from the test window or lost focus, potentially indicating distraction or looking for external help."
      />
      <MetricCard
        title="Tab Switches"
        count={tabSwitchCount}
        icon={TabletSmartphone}
        tooltip="Number of times the candidate switched between browser tabs, which may indicate they were accessing external resources during the test."
      />
      <MetricCard
        title="Clipboard Actions"
        count={clipboardCount}
        icon={Clipboard}
        tooltip="Number of copy, paste, or cut operations performed, which could indicate potential plagiarism or use of external content."
      />
    </div>
  );
}